import React, { useContext, useState, useEffect, useLayoutEffect, useReducer, useRef, useCallback, useMemo } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green } from "@material-ui/core/colors";
import {
  Button,
  Divider,
  Typography,
  IconButton,
  Fab,
  makeStyles,
  useTheme,
  useMediaQuery
} from "@material-ui/core";

import {
  AccessTime,
  Done,
  DoneAll,
  ExpandLess,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  WhatsApp,
  Error as ErrorIcon,
  SentimentSatisfiedAlt,
  KeyboardArrowDown,
  Flag
} from "@material-ui/icons";
import { red } from "@material-ui/core/colors";
import Tooltip from "@material-ui/core/Tooltip";
import LockIcon from '@material-ui/icons/Lock';
import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import MessageReactions from "../MessageReactions";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";
import PdfPreview from "../PdfPreview";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import AdMetaPreview from "../AdMetaPreview";
import MessageOperatorAvatar from "./MessageOperatorAvatar";
import FacebookPostPreview from "../FacebookPostPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import { CircularProgress } from "@material-ui/core";
import { useParams, useHistory } from 'react-router-dom';
import Template from "./templates";
import InteractiveMessagePreview from "./InteractiveMessagePreview";
import PollMessageCard from "../PollMessageCard";
import { usePdfViewer } from "../../hooks/usePdfViewer";
import { MF_PENDING_TICKET_MESSAGE_SCROLL_KEY } from "../../constants/pendingTicketMessageScroll";
import useImageGrouping from "./useImageGrouping";
import ImageGrid from "./ImageGrid";
import { resolveMessageOperatorUser } from "../../utils/avatar";

const AUTOMATED_ORIGIN_META = {
  schedule: { label: "Mensagem agendada", Icon: Flag },
  campaign: { label: "Mensagem de campanha", Icon: Flag },
  campaignPro: { label: "Campanha PRO", Icon: Flag }
};

const isImageMediaType = (mediaType) =>
  mediaType === "image" || mediaType === "imageMessage";
const isVideoMediaType = (mediaType) =>
  mediaType === "video" || mediaType === "videoMessage";

const resolveAutomatedOrigin = (message) => {
  if (message?.automatedOrigin) return message.automatedOrigin;
  try {
    const raw = message?.dataJson;
    if (!raw) return null;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed?.multiflowAutomatedOrigin) return parsed.multiflowAutomatedOrigin;
  } catch {
    /* ignore */
  }
  if (
    message?.fromMe &&
    typeof message?.body === "string" &&
    /^\s*agendad/i.test(message.body)
  ) {
    return "schedule";
  }
  return null;
};

const getOutgoingBubbleClass = (message, classes) => {
  if (resolveAutomatedOrigin(message)) return classes.messageRightAutomated;
  if (message.isPrivate) return classes.messageRightPrivate;
  return classes.messageRight;
};

// Paleta de cores para nomes de membros em grupos (estilo WhatsApp)
const GROUP_MEMBER_COLORS = [
  "#e17076", // vermelho suave
  "#7bc862", // verde
  "#e5c07b", // amarelo
  "#6ec9cb", // teal
  "#65aadd", // azul
  "#ee7aae", // rosa
  "#dda15e", // laranja
  "#a695e7", // roxo
  "#56b4c2", // ciano
  "#e88b72", // salmao
  "#82c366", // verde claro
  "#c89cf4", // lilas
];

function getContactColor(contactId) {
  if (!contactId) return GROUP_MEMBER_COLORS[0];
  const str = String(contactId);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return GROUP_MEMBER_COLORS[Math.abs(hash) % GROUP_MEMBER_COLORS.length];
}

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
    // ✅ CORREÇÃO: Garantir que o wrapper não interfira no background
    backgroundColor: "transparent",
  },

  currentTick: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "95%",
    backgroundColor: theme.palette.primary.main,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "1px 5px 10px #b3b3b3",
  },

  currentTicktText: {
    color: theme.palette.primary,
    fontWeight: 'bold',
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundRepeat: "repeat",
    backgroundSize: "auto",
    backgroundPosition: "0 0",
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    backgroundAttachment: "fixed", // ✅ CORREÇÃO: Fixar background para evitar que suma
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 30px 20px",
    overflowY: "scroll",
    position: "relative", // ✅ CORREÇÃO: Garantir contexto de posicionamento
    ...theme.scrollbarStyles,
  },
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: "5px dashed #333",
    color: '#333',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  scrollToBottomFab: {
    position: "absolute",
    right: 12,
    bottom: 12,
    zIndex: 25,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    marginBottom: 2, // ✅ CORREÇÃO: Adicionar marginBottom padrão
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    marginBottom: 2, // ✅ CORREÇÃO: Adicionar marginBottom padrão
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  messageRightRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    width: "100%",
    gap: 6,
    marginTop: 2,
    marginBottom: 2,
    boxSizing: "border-box",
  },

  messageRightInRow: {
    marginLeft: 0,
    marginTop: 0,
    marginBottom: 0,
    flex: "0 1 auto",
    maxWidth: 600,
    minWidth: 0,
    alignSelf: "auto",
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    marginBottom: 2, // ✅ CORREÇÃO: Adicionar marginBottom padrão
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  messageRightAutomated: {
    marginLeft: 20,
    marginTop: 2,
    marginBottom: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 28,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  automatedOriginBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 152, 0, 0.45)",
    color: "#8B6914",
    zIndex: 2,
    pointerEvents: "auto",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  /** Citação clicável: saltar à mensagem original (estilo WhatsApp) */
  quotedMessageClickable: {
    cursor: "pointer",
    outline: "none",
    "&:hover": {
      filter: theme.mode === "light" ? "brightness(0.97)" : "brightness(1.06)",
    },
    "&:focus-visible": {
      boxShadow: "0 0 0 2px rgba(0, 102, 255, 0.5)",
      borderRadius: "7.5px",
    },
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  emojiButtonLeft: {
    position: "absolute",
    right: "-27px",
    top: "5px",
    transform: "none",
    padding: 2,
    backgroundColor: theme.palette.type === "light" ? "rgba(255, 255, 255, 0.9)" : "rgba(32, 44, 51, 0.9)",
    borderRadius: "50%",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
    "&:hover": {
      backgroundColor: theme.palette.type === "light" ? "rgba(240, 242, 245, 0.9)" : "rgba(56, 76, 86, 0.9)",
    }
  },

  messageWrapperWithReaction: {
    marginBottom: 35, // ✅ CORREÇÃO: Aumentado de 22 para 35 para dar espaço para a reação (bottom: -20px + padding)
    paddingBottom: 8, // ✅ CORREÇÃO: Adicionar padding inferior para evitar que reação seja coberta
  },

  reactionBubble: {
    position: "absolute",
    bottom: "-20px",
    left: "15px",
    backgroundColor: theme.palette.type === "light" ? "rgba(255, 255, 255, 0.9)" : "rgba(32, 44, 51, 0.9)",
    borderRadius: "50%",
    padding: "3px 8px",
    fontSize: "14px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
    zIndex: 10, // ✅ CORREÇÃO: Garantir que a reação fique acima das mensagens seguintes
  },

  reactionBubbleRight: {
    position: "absolute",
    bottom: "-20px",
    right: "15px", // ✅ CORREÇÃO: Para mensagens enviadas, posicionar à direita
    backgroundColor: theme.palette.type === "light" ? "rgba(255, 255, 255, 0.9)" : "rgba(32, 44, 51, 0.9)",
    borderRadius: "50%",
    padding: "3px 8px",
    fontSize: "14px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
    zIndex: 10, // ✅ CORREÇÃO: Garantir que a reação fique acima das mensagens seguintes
  },

  messageContactName: {
    display: "flex",
    fontWeight: 500,
    // cor aplicada via inline style por getContactColor()
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    // ✅ CORREÇÃO: objectFit removido para vídeos funcionarem melhor
    // objectFit: "cover", // Removido pois pode causar problemas em vídeos
    width: 400,
    height: "auto",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    // ✅ CORREÇÃO: Adicionar estilos específicos para vídeo
    "&[controls]": {
      objectFit: "contain", // Para vídeos, usar contain em vez de cover
    }
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackErrorIcon: {
    color: red[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deliveryFailHint: {
    display: "block",
    fontSize: "11px",
    lineHeight: 1.3,
    color: red[700],
    marginTop: 4,
    fontWeight: 500,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  }
}));

const normMessageBody = (b) => String(b || "").replace(/\r\n/g, "\n").trim();

const stripSignPrefixForMatch = (b) => {
  const n = normMessageBody(b);
  const m = n.match(/^\*[^*\n]+:\*\n?([\s\S]*)$/);
  return m ? normMessageBody(m[1]) : n;
};

const isMediaPlaceholderBody = (b) => {
  const t = normMessageBody(b).toLowerCase();
  return /^\[(mídia|media|photo|video|audio|document|image|documento|foto|vídeo)\]$/.test(t);
};

const incomingHasMedia = (incoming) =>
  !!(incoming?.mediaUrl && String(incoming.mediaUrl).trim() !== "") ||
  !!(
    incoming?.mediaType &&
    !["chat", "conversation", "extendedTextMessage"].includes(String(incoming.mediaType))
  );

function shouldReplaceOptimisticWithIncoming(opt, incoming, pendingOptimisticCount = 1) {
  if (!opt?.optimistic || !incoming?.fromMe) return false;

  const hasIncomingMedia = incomingHasMedia(incoming);
  const singlePending = pendingOptimisticCount === 1;

  if (opt.mediaKind === "media") {
    return hasIncomingMedia || isMediaPlaceholderBody(incoming.body) || singlePending;
  }

  if (singlePending && !hasIncomingMedia) return true;

  const optStripped = stripSignPrefixForMatch(opt.body);
  const inStripped = stripSignPrefixForMatch(incoming.body);
  if (optStripped && inStripped && optStripped === inStripped) return true;
  return normMessageBody(opt.body) === normMessageBody(incoming.body);
}

function findOptimisticToReplace(state, incoming) {
  const pending = state.filter((m) => m.optimistic && m.fromMe);
  if (!pending.length || !incoming?.fromMe) return null;

  if (pending.length === 1) {
    return shouldReplaceOptimisticWithIncoming(pending[0], incoming, 1) ? pending[0] : null;
  }

  return (
    pending.find((opt) =>
      shouldReplaceOptimisticWithIncoming(opt, incoming, pending.length)
    ) || null
  );
}

function releaseOptimisticEntry(clientTempId, optimisticBlobUrlsRef, optimisticOrderRef) {
  if (!clientTempId) return;
  const url = optimisticBlobUrlsRef?.current?.get(clientTempId);
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
    optimisticBlobUrlsRef.current.delete(clientTempId);
  }
  if (optimisticOrderRef?.current) {
    optimisticOrderRef.current = optimisticOrderRef.current.filter(
      (x) => x.clientTempId !== clientTempId
    );
  }
}

// ✅ Ordenação cronológica; empate em createdAt (mesmo segundo) usa updatedAt, id numérico e wid.
const sortMessagesByCreatedAt = (messages) => {
  return [...messages].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (dateA !== dateB) {
      return dateA - dateB;
    }
    const uA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const uB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    if (uA !== uB) {
      return uA - uB;
    }
    const idA = typeof a.id === "number" ? a.id : Number(a.id);
    const idB = typeof b.id === "number" ? b.id : Number(b.id);
    if (!Number.isNaN(idA) && !Number.isNaN(idB)) {
      return idA - idB;
    }
    return String(a.wid || "").localeCompare(String(b.wid || ""));
  });
};

/** Snapshot mínimo da mensagem citada para UI + scroll (evita depender do socket trazer `quotedMsg` completo). */
function buildQuotedSnapshotFromMessage(orig) {
  if (!orig || orig.id == null) return null;
  return {
    id: orig.id,
    wid: orig.wid,
    body: orig.body,
    mediaType: orig.mediaType,
    mediaUrl: orig.mediaUrl,
    fromMe: orig.fromMe,
    contact: orig.contact
      ? {
          id: orig.contact.id,
          name: orig.contact.name,
          number: orig.contact.number
        }
      : null
  };
}

/** Se a mensagem tem `quotedMsgId` mas veio sem `quotedMsg.id`, preenche a partir da lista já carregada. */
function hydrateQuotedMsgOnMessage(message, list) {
  if (!message || message.id == null) return message;
  if (message.quotedMsg?.id != null) return message;
  const qid = message.quotedMsgId;
  if (qid == null || String(qid).trim() === "") return message;
  const s = String(qid);
  const orig = list.find(
    (m) =>
      m != null &&
      m.id != null &&
      (String(m.id) === s || (m.wid != null && String(m.wid) === s))
  );
  if (!orig) return message;
  return { ...message, quotedMsg: buildQuotedSnapshotFromMessage(orig) };
}

/**
 * Evento `company-*-appMessage` é broadcast por empresa: filtrar com rigor evita
 * mensagens de outro ticket entrarem na lista aberta (cruzamento só em tela).
 * Com `ticketDbId` definido, exige match numérico em `message.ticketId` (ou uuid na rota se id vier vazio).
 */
function socketAppMessageTargetsTicket(data, ticketIdRoute, ticketDbIdNum) {
  if (!data) return false;
  const { action: evtAction } = data;

  if (evtAction === "delete") {
    const msg = data.message;
    if (msg && (msg.ticketId != null || msg.ticket)) {
      return socketPayloadMessageBelongsToTicket(msg, data.ticket, ticketIdRoute, ticketDbIdNum);
    }
    if (ticketDbIdNum != null && data.ticketId != null) {
      return Number(data.ticketId) === Number(ticketDbIdNum);
    }
    return false;
  }

  const msg = data.message;
  if (!msg) return false;

  if (ticketDbIdNum != null) {
    const evtTid =
      data.ticketId ?? msg.ticketId ?? data.ticket?.id ?? msg.ticket?.id;
    if (evtTid != null && evtTid !== "") {
      return Number(evtTid) === Number(ticketDbIdNum);
    }
  }

  return socketPayloadMessageBelongsToTicket(msg, data.ticket, ticketIdRoute, ticketDbIdNum);
}

function socketPayloadMessageBelongsToTicket(msg, topTicket, ticketIdRoute, ticketDbIdNum) {
  const mTid = msg.ticketId ?? msg.ticket?.id;
  const mUuid = msg.ticket?.uuid;
  const topTid = topTicket?.id;
  const topUuid = topTicket?.uuid;

  if (ticketDbIdNum != null) {
    if (mTid != null && mTid !== "") {
      return Number(mTid) === Number(ticketDbIdNum);
    }
    if (topTid != null && topTid !== "") {
      return Number(topTid) === Number(ticketDbIdNum);
    }
    if (
      mUuid != null &&
      ticketIdRoute != null &&
      String(ticketIdRoute) !== "undefined"
    ) {
      return String(mUuid) === String(ticketIdRoute);
    }
    if (
      topUuid != null &&
      ticketIdRoute != null &&
      String(ticketIdRoute) !== "undefined"
    ) {
      return String(topUuid) === String(ticketIdRoute);
    }
    return false;
  }

  if (ticketIdRoute == null || String(ticketIdRoute) === "undefined") {
    return false;
  }

  return (
    topTicket?.uuid === ticketIdRoute ||
    topTicket?.id === ticketIdRoute ||
    String(topTicket?.id ?? "") === String(ticketIdRoute) ||
    msg.ticket?.uuid === ticketIdRoute ||
    msg.ticket?.id === ticketIdRoute ||
    String(msg.ticket?.id ?? "") === String(ticketIdRoute) ||
    String(msg.ticket?.uuid ?? "") === String(ticketIdRoute)
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];
    const baseState = state.filter((m) => !m.optimistic);

    messages.forEach((message) => {
      const messageIndex = baseState.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        baseState[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    const allMessages = [...newMessages, ...baseState];
    return sortMessagesByCreatedAt(allMessages);
  }

  if (action.type === "ADD_OPTIMISTIC_MESSAGE") {
    const m = action.payload?.message;
    if (m == null || m.id == null) {
      return state;
    }
    return sortMessagesByCreatedAt([...state, m]);
  }

  if (action.type === "REMOVE_OPTIMISTIC_CLIENT_ID") {
    const id = action.payload;
    if (id == null) return state;
    return sortMessagesByCreatedAt(
      state.filter((m) => m.clientTempId !== id && m.id !== id)
    );
  }

  if (action.type === "ADD_MESSAGE") {
    const raw = action.payload;
    const newMessage = raw && raw.message !== undefined ? raw.message : raw;
    const guardTicketDbId =
      raw && raw.message !== undefined ? raw.guardTicketDbId : undefined;
    if (
      guardTicketDbId != null &&
      newMessage?.ticketId != null &&
      Number(newMessage.ticketId) !== Number(guardTicketDbId)
    ) {
      return state;
    }
    if (newMessage == null || newMessage.id == null) {
      return state;
    }
    let incomingMessage = newMessage;
    let messageIndex = state.findIndex((m) => m.id === incomingMessage.id);
    // Evitar duplicata no front: mesma mensagem pode chegar 2x (envio + eco do WhatsApp); deduplicar por wid
    if (messageIndex === -1 && incomingMessage.wid) {
      messageIndex = state.findIndex((m) => m.wid === incomingMessage.wid);
    }
    // Eco Baileys/socket pode criar 2ª bolha outbound com outro id e sem wid
    if (
      messageIndex === -1 &&
      newMessage.fromMe &&
      !newMessage.mediaUrl &&
      !newMessage.mediaType
    ) {
      const normBody = (b) => String(b || "").replace(/\r\n/g, "\n").trim();
      const newBody = normBody(newMessage.body);
      if (newBody) {
        const newTs = newMessage.createdAt
          ? new Date(newMessage.createdAt).getTime()
          : 0;
        messageIndex = state.findIndex((m) => {
          if (!m.fromMe || m.id === newMessage.id) return false;
          if (normBody(m.body) !== newBody) return false;
          if (m.mediaUrl || m.mediaType) return false;
          const mTs = m.createdAt ? new Date(m.createdAt).getTime() : 0;
          return Math.abs(mTs - newTs) < 15000;
        });
      }
    }

    if (messageIndex === -1 && incomingMessage.fromMe) {
      const opt = findOptimisticToReplace(state, incomingMessage);
      if (opt) {
        if (!incomingMessage.quotedMsg?.id && opt.quotedMsg?.id) {
          incomingMessage = {
            ...incomingMessage,
            quotedMsg: opt.quotedMsg,
            quotedMsgId:
              incomingMessage.quotedMsgId ?? opt.quotedMsgId ?? opt.quotedMsg.id
          };
        }
        const optKey = opt.clientTempId || opt.id;
        state = state.filter(
          (m) => m.clientTempId !== optKey && m.id !== optKey
        );
      }
    }

    let toStore = { ...incomingMessage };
    const prev = messageIndex !== -1 ? state[messageIndex] : null;
    if (prev?.user && !toStore.user) {
      toStore = {
        ...toStore,
        user: prev.user,
        userId: toStore.userId ?? prev.userId,
      };
    }
    if (prev?.quotedMsg?.id && !toStore.quotedMsg?.id) {
      const prevQ = prev.quotedMsgId;
      const newQ = toStore.quotedMsgId;
      if (
        (prevQ != null && newQ != null && String(prevQ) === String(newQ)) ||
        (toStore.quotedMsgId == null && prevQ != null)
      ) {
        toStore = {
          ...toStore,
          quotedMsg: prev.quotedMsg,
          quotedMsgId: toStore.quotedMsgId ?? prev.quotedMsgId
        };
      }
    }
    toStore = hydrateQuotedMsgOnMessage(toStore, state);

    if (messageIndex !== -1) {
      state[messageIndex] = toStore;
    } else {
      state.push(toStore);
    }
    return sortMessagesByCreatedAt(state);
  }

  if (action.type === "UPDATE_MESSAGE") {
    const raw = action.payload;
    const messageToUpdate =
      raw && raw.message !== undefined ? raw.message : raw;
    const guardTicketDbId =
      raw && raw.message !== undefined ? raw.guardTicketDbId : undefined;
    if (messageToUpdate == null || messageToUpdate.id == null) {
      return state;
    }
    if (
      guardTicketDbId != null &&
      messageToUpdate?.ticketId != null &&
      Number(messageToUpdate.ticketId) !== Number(guardTicketDbId)
    ) {
      return state;
    }
    let messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);
    // Fallback: em alguns fluxos o backend pode emitir "update" antes do "create".
    // Nesses casos, tentamos casar por wid para manter atualização em tempo real.
    if (messageIndex === -1 && messageToUpdate?.wid) {
      messageIndex = state.findIndex((m) => m.wid === messageToUpdate.wid);
    }

    if (messageIndex !== -1) {
      const prev = state[messageIndex];
      let merged = { ...messageToUpdate };
      if (
        merged.ack === -1 &&
        prev?.dataJson &&
        !merged.dataJson
      ) {
        merged = { ...merged, dataJson: prev.dataJson };
      }
      if (prev?.quotedMsg?.id && !merged.quotedMsg?.id) {
        const prevQ = prev.quotedMsgId;
        const newQ = merged.quotedMsgId;
        if (
          (prevQ != null && newQ != null && String(prevQ) === String(newQ)) ||
          (merged.quotedMsgId == null && prevQ != null)
        ) {
          merged = {
            ...merged,
            quotedMsg: prev.quotedMsg,
            quotedMsgId: merged.quotedMsgId ?? prev.quotedMsgId
          };
        }
      }
      if (prev?.user && !merged.user) {
        merged = {
          ...merged,
          user: prev.user,
          userId: merged.userId ?? prev.userId
        };
      }
      merged = hydrateQuotedMsgOnMessage(merged, state);
      state[messageIndex] = merged;
    } else if (
      messageToUpdate.body?.trim() ||
      messageToUpdate.mediaUrl ||
      (messageToUpdate.mediaType &&
        messageToUpdate.mediaType !== "conversation" &&
        messageToUpdate.mediaType !== "extendedTextMessage")
    ) {
      // Meow grupo: 2º webhook preenche stub — "update" chega sem "create" na lista
      const merged = hydrateQuotedMsgOnMessage({ ...messageToUpdate }, state);
      state.push(merged);
    }

    return sortMessagesByCreatedAt(state);
  }

  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload;
    const filteredMessages = state.filter((m) => m.id !== messageId);
    return sortMessagesByCreatedAt(filteredMessages);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

/**
 * Mensagem citada (reply): o backend grava tipos no estilo Baileys (imageMessage, videoMessage…)
 * e o WuzAPI grava às vezes "image", "video". O UI só comparava com "image"/"video",
 * então a prévia de mídia não renderizava e caía no body (legenda ou texto errado).
 */
function quotedMediaIsAudio(mt) {
  if (mt == null || mt === "") return false;
  const s = String(mt).toLowerCase();
  return s === "audio" || s === "audiomessage" || s === "ptt" || s === "voice" || s.includes("opus");
}
function quotedMediaIsVideo(mt) {
  if (mt == null || mt === "") return false;
  const s = String(mt).toLowerCase();
  return s === "video" || s === "videomessage" || (s.includes("video") && !s.includes("image"));
}
function quotedMediaIsImage(mt, mediaUrl) {
  if (mt != null && mt !== "") {
    const s = String(mt).toLowerCase();
    if (s === "image" || s === "imagemessage" || s === "stickermessage") return true;
    if (s === "viewoncemessage" || s === "viewoncemessagev2") return true;
  }
  if (mediaUrl && typeof mediaUrl === "string" && /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(mediaUrl)) {
    return true;
  }
  return false;
}
function quotedMediaIsDocument(mt) {
  if (mt == null || mt === "") return false;
  const s = String(mt).toLowerCase();
  return s === "application" || s === "document" || s === "documentmessage";
}
function quotedMediaIsContact(mt) {
  if (mt == null || mt === "") return false;
  const s = String(mt).toLowerCase();
  return (
    s === "contactmessage" ||
    s === "contact" ||
    s === "contactsarraymessage"
  );
}

/** Extrai nome e número exibíveis de um bloco vCard (mesma heurística do contactMessage único). */
function parseVcardBodyFields(body) {
  if (!body || typeof body !== "string") {
    return { contact: "", number: undefined };
  }
  const array = body.split("\n");
  const obj = [];
  let contact = "";
  for (let index = 0; index < array.length; index++) {
    const v = array[index];
    const values = v.split(":");
    for (let ind = 0; ind < values.length; ind++) {
      if (values[ind].indexOf("+") !== -1) {
        obj.push({ number: values[ind] });
      }
      if (values[ind].indexOf("FN") !== -1) {
        contact = values[ind + 1];
      }
    }
  }
  let number = obj[0]?.number;
  if (!number) {
    const waid = body.match(/(?:waid|WAID)=(\d+)/);
    if (waid) number = `+${waid[1]}`;
  }
  return { contact, number };
}

/** Divide o body em um vCard por cartão (múltiplos contatos do WhatsApp). */
function splitVcardBlocks(raw) {
  const t = (raw || "").trim();
  if (!t) return [];
  return t
    .split(/(?=BEGIN:VCARD)/gi)
    .map((b) => b.trim())
    .filter((b) => /^BEGIN:VCARD/i.test(b));
}

function isAudioMessageType(message) {
  const mt = message?.mediaType != null ? String(message.mediaType).toLowerCase() : "";
  if (["audio", "audiomessage", "ptt", "voice"].includes(mt)) return true;
  if (message?.mediaUrl) {
    const url = String(message.mediaUrl).toLowerCase();
    if ([".mp3", ".wav", ".ogg", ".m4a", ".aac", ".webm"].some((ext) => url.includes(ext))) {
      return true;
    }
  }
  return false;
}

function isVcardMessage(message) {
  if (
    message.mediaType === "contactMessage" ||
    message.mediaType === "contactsArrayMessage"
  ) {
    return true;
  }
  const body = message?.body;
  return (
    typeof body === "string" &&
    body.trim().length > 0 &&
    /BEGIN:VCARD/i.test(body)
  );
}

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  ticketStatus,
  /** ID numérico do ticket (DB); reforça o match do socket quando o payload vem só com ticketId na mensagem */
  ticketDbId
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  /** Incrementado no reconnect do socket para forçar nova leitura da API (evita lista sem mensagens novas após restart do backend). */
  const [reloadNonce, setReloadNonce] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showScrollToBottomFab, setShowScrollToBottomFab] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();
  /** Pesquisa no drawer do contato: ir até mensagem no chat (carrega páginas antigas se necessário). */
  const pendingSearchScrollRef = useRef(null);
  const [searchScrollTick, setSearchScrollTick] = useState(0);

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const [messageWithReactionOpen, setMessageWithReactionOpen] = useState(null);
  const [reactionAnchorEl, setReactionAnchorEl] = useState(null);
  const { ticketId } = useParams();

  const currentTicketId = useRef(ticketId);
  /** Fila FIFO para casar eco do socket com mensagem otimista (mesmo texto, vários envios). */
  const optimisticOrderRef = useRef([]);
  /** Revogar blob: URLs de preview otimista de mídia após substituição ou erro. */
  const optimisticBlobUrlsRef = useRef(new Map());
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);

  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  // ✅ Cache para evitar logs duplicados de erros de vídeo
  const videoErrorLogCache = useRef(new Set());

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const [imageGroupingEnabled, setImageGroupingEnabled] = useState(true);
  const imageGroupMap = useImageGrouping(messagesList, imageGroupingEnabled, isGroup);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);
  const [queuesCache, setQueuesCache] = useState(new Map()); // ✅ Cache de filas para evitar múltiplas requisições

  // Hook simplificado para PDF
  const {
    downloadPdf,
    extractPdfInfoFromMessage,
    isPdfUrl,
    getDocumentDownloadFilename,
    isAttachmentDocumentMessage,
  } = usePdfViewer();

  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const [diskUsage, setDiskUsage] = useState(null);

  /** Janela de 24h (Meta): contador para banner - só para whatsapp_oficial, facebook, instagram */
  const [window24h, setWindow24h] = useState(null);  const META_CHANNELS = ["whatsapp_oficial", "facebook", "instagram"];
  const isMetaChannel = channel && META_CHANNELS.includes(channel);

  /** No mobile: texto da política de 24h recolhido por padrão; ícone para expandir */
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [policyTextExpanded, setPolicyTextExpanded] = useState(false);

  // Buscar uso de disco - intervalo maior e só quando aba visível (evita travar com muitos usuários)
  const DISK_USAGE_POLL_MS = 300000; // 5 min — evita picos de CPU com muitos usuários (socket atualiza se necessário)
  useEffect(() => {
    const fetchDiskUsage = async () => {
      if (user && user.companyId) {
        try {
          const { data } = await api.get(
            `/companies/${user.companyId}/disk-usage`
          );
          setDiskUsage(data);
        } catch (error) {
          // Ignorar erro silenciosamente
        }
      }
    };
    fetchDiskUsage();
    let interval = null;
    const startPolling = () => {
      if (!interval) interval = setInterval(fetchDiskUsage, DISK_USAGE_POLL_MS);
    };
    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    if (typeof document !== "undefined" && document.hidden === false) startPolling();
    const onVisibility = () => (document.hidden ? stopPolling() : (fetchDiskUsage(), startPolling()));
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  // Buscar status da janela de 24h (contador no banner) para canais Meta
  const fetch24hWindow = useCallback(async () => {
    if (!ticketId || !isMetaChannel) {
      setWindow24h(null);
      return;
    }
    try {
      const { data } = await api.get(`/tickets/${ticketId}/24h-window`);
      setWindow24h(data);
    } catch (err) {
      setWindow24h(null);
    }
  }, [ticketId, isMetaChannel]);

  useEffect(() => {
    fetch24hWindow();
    if (!ticketId || !isMetaChannel) return;
    const interval = setInterval(fetch24hWindow, 60000); // atualiza a cada 1 min
    return () => clearInterval(interval);
  }, [fetch24hWindow, ticketId, isMetaChannel]);

  // Re-fetch 24h ao receber nova mensagem do contato (janela reinicia)
  useEffect(() => {
    if (!socket || !user?.companyId || !ticketId || !isMetaChannel) return;
    const onAppMessage = (data) => {
      const isThisTicket = socketAppMessageTargetsTicket(
        data,
        ticketId,
        ticketDbId
      );
      if (data.action === "create" && isThisTicket && !data.message?.fromMe) {
        setTimeout(fetch24hWindow, 1500);
      }
    };
    socket.on(`company-${user.companyId}-appMessage`, onAppMessage);
    return () => socket.off(`company-${user.companyId}-appMessage`, onAppMessage);
  }, [socket, user?.companyId, ticketId, ticketDbId, isMetaChannel, fetch24hWindow]);

  // Escutar evento de atualização de uso de disco via socket
  useEffect(() => {
    if (socket && user?.companyId) {
      const handleDiskUsageUpdate = (data) => {
        if (data.companyId === user.companyId && data.diskUsage) {
          console.log("[MessagesList] Atualizando uso de disco via socket:", data.diskUsage);
          // mescla: o evento pode não trazer checkDiskQuota (mantém o que veio do GET)
          setDiskUsage((prev) => ({ ...(prev || {}), ...data.diskUsage }));
        }
      };

      socket.on("diskUsageUpdated", handleDiskUsageUpdate);

      return () => {
        socket.off("diskUsageUpdated", handleDiskUsageUpdate);
      };
    }
  }, [socket, user?.companyId]);

  useEffect(() => {
    async function fetchData() {
      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {
        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
        if (key === "groupConsecutiveImages") setImageGroupingEnabled(value !== "disabled")
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
  }, [companyId, getAll]);

  useEffect(() => {
    optimisticBlobUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    });
    optimisticBlobUrlsRef.current.clear();
    optimisticOrderRef.current = [];
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = ticketId;
    pendingSearchScrollRef.current = null;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (!socket || !ticketId || ticketId === "undefined") return;
    const onReconnect = () => {
      setPageNumber(1);
      setReloadNonce((n) => n + 1);
    };
    socket.on("reconnect", onReconnect);
    return () => socket.off("reconnect", onReconnect);
  }, [socket, ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);
          }

          let skipScrollToBottomForPending = false;
          if (pageNumber === 1 && currentTicketId.current === ticketId) {
            try {
              const raw = sessionStorage.getItem(MF_PENDING_TICKET_MESSAGE_SCROLL_KEY);
              if (raw) {
                const parsed = JSON.parse(raw);
                const apiTicketId = data.ticket?.id;
                if (
                  apiTicketId != null &&
                  parsed?.ticketDbId != null &&
                  String(parsed.ticketDbId) === String(apiTicketId)
                ) {
                  sessionStorage.removeItem(MF_PENDING_TICKET_MESSAGE_SCROLL_KEY);
                  pendingSearchScrollRef.current = {
                    messageId: String(parsed.messageId),
                    loadsIssued: 0,
                    maxLoads: 80,
                  };
                  setSearchScrollTick((t) => t + 1);
                  skipScrollToBottomForPending = true;
                }
              }
            } catch (_) {
              try {
                sessionStorage.removeItem(MF_PENDING_TICKET_MESSAGE_SCROLL_KEY);
              } catch (__) {}
            }
          }

          if (
            pageNumber === 1 &&
            data.messages.length > 1 &&
            !skipScrollToBottomForPending
          ) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage, reloadNonce, history]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    if (!socket || typeof socket.on !== "function" || companyId === null || companyId === undefined) {
      return;
    }

    const companyIdStr = String(companyId);

    const joinChatForTicket = () => {
      if (socket && typeof socket.emit === "function") {
        socket.emit("joinChatBox", `${ticketId}`);
      }
    };

    const onAppMessageMessagesList = (data) => {
      const isThisTicket = socketAppMessageTargetsTicket(
        data,
        ticketId,
        ticketDbId
      );

      if (data.action === "create" && isThisTicket) {
        const incoming = data.message;
        if (incoming?.fromMe && optimisticOrderRef.current.length) {
          const pendingCount = optimisticOrderRef.current.length;
          const queueFirst = optimisticOrderRef.current[0];
          if (
            queueFirst &&
            shouldReplaceOptimisticWithIncoming(
              {
                optimistic: true,
                mediaKind: queueFirst.mediaKind,
                body: queueFirst.body,
                fromMe: true
              },
              incoming,
              pendingCount
            )
          ) {
            releaseOptimisticEntry(
              queueFirst.clientTempId,
              optimisticBlobUrlsRef,
              optimisticOrderRef
            );
            dispatch({
              type: "REMOVE_OPTIMISTIC_CLIENT_ID",
              payload: queueFirst.clientTempId
            });
          }
        }
        dispatch({
          type: "ADD_MESSAGE",
          payload: { message: data.message, guardTicketDbId: ticketDbId }
        });
        scrollToBottom();
      }

      if (data.action === "update" && isThisTicket) {
        dispatch({
          type: "UPDATE_MESSAGE",
          payload: { message: data.message, guardTicketDbId: ticketDbId }
        });
      }

      if (data.action === "delete" && isThisTicket) {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    };

    const onMessageError = (data) => {
      const sameTicket =
        ticketDbId != null &&
        data.ticketId != null &&
        Number(data.ticketId) === Number(ticketDbId);
      if (sameTicket) {
        const baseMessage = data.errorMessage || i18n.t("messagesList.messageFailed");
        const displayMessage = data.errorCode != null
          ? `(#${data.errorCode}) ${baseMessage}`
          : baseMessage;
        toast.error(displayMessage, {
          toastId: `msg-error-${data.messageId}`,
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    };

    joinChatForTicket();
    socket.on("connect", joinChatForTicket);
    socket.on(`company-${companyIdStr}-appMessage`, onAppMessageMessagesList);
    socket.on(`company-${companyIdStr}-messageError`, onMessageError);

    return () => {
      if (socket && typeof socket.emit === "function") {
        socket.emit("joinChatBoxLeave", `${ticketId}`);
      }
      if (socket && typeof socket.off === "function") {
        socket.off("connect", joinChatForTicket);
        socket.off(`company-${companyIdStr}-appMessage`, onAppMessageMessagesList);
        socket.off(`company-${companyIdStr}-messageError`, onMessageError);
      }
    };
  }, [ticketId, ticketDbId, socket, companyId]);

  // ✅ CORREÇÃO CRÍTICA: Garantir que o background seja sempre aplicado, mesmo após re-renderizações
  // Pré-carregar as imagens e garantir que estejam disponíveis antes de aplicar
  useEffect(() => {
    const messagesListElement = document.getElementById("messagesList");
    if (!messagesListElement) return;

    const backgroundImageUrl = theme.mode === 'light' ? whatsBackground : whatsBackgroundDark;
    const backgroundColor = theme.mode === 'light' ? 'transparent' : '#0b0b0d';

    // ✅ CORREÇÃO: Aplicar background diretamente sem pré-carregamento (assets locais não precisam)
    // Assets processados pelo Webpack já estão disponíveis e não precisam de CORS
    const applyBackground = () => {
      const backgroundImage = `url(${backgroundImageUrl})`;
      // Aplicar o background diretamente no elemento DOM com !important via setProperty
      messagesListElement.style.setProperty('background-image', backgroundImage, 'important');
      messagesListElement.style.setProperty('background-repeat', 'repeat', 'important');
      messagesListElement.style.setProperty('background-size', 'auto', 'important');
      messagesListElement.style.setProperty('background-position', '0 0', 'important');
      messagesListElement.style.setProperty('background-color', backgroundColor, 'important');
      messagesListElement.style.setProperty('background-attachment', 'fixed', 'important');
    };

    // ✅ CORREÇÃO: Aplicar imediatamente e verificar se a imagem está disponível
    applyBackground();

    // ✅ CORREÇÃO: Verificar se a imagem está carregada (sem crossOrigin para assets locais)
    const verifyImage = () => {
      const img = new Image();
      // ✅ CORREÇÃO: Não usar crossOrigin para assets locais (causa erro)
      img.onload = () => {
        // Imagem carregada com sucesso, garantir que o background está aplicado
        applyBackground();
      };
      img.onerror = (error) => {
        console.warn(`[MessagesList] Aviso: Imagem de background pode não estar disponível: ${backgroundImageUrl}`, error);
        // Aplicar mesmo assim (pode estar em cache do navegador)
        applyBackground();
      };
      img.src = backgroundImageUrl;
    };

    // Verificar após um pequeno delay para garantir que o DOM está pronto
    setTimeout(verifyImage, 100);

    // ✅ CORREÇÃO: Observar mudanças no elemento para reaplicar o background se necessário
    let reapplyTimeout = null;
    const observer = new MutationObserver((mutations) => {
      let shouldReapply = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          // Verificar se o backgroundImage foi removido ou alterado
          const currentBg = messagesListElement.style.backgroundImage;
          const computedBg = window.getComputedStyle(messagesListElement).backgroundImage;
          if (!currentBg || currentBg === 'none' || currentBg === '' ||
            computedBg === 'none' || computedBg === '') {
            shouldReapply = true;
          }
        }
      });
      if (shouldReapply) {
        // ✅ CORREÇÃO: Usar debounce para evitar reaplicações excessivas
        if (reapplyTimeout) clearTimeout(reapplyTimeout);
        reapplyTimeout = setTimeout(() => {
          applyBackground();
        }, 100);
      }
    });

    // Observar mudanças no atributo style e no DOM
    observer.observe(messagesListElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: false
    });

    // ✅ CORREÇÃO: Reaplicar periodicamente como fallback (a cada 1 segundo)
    const interval = setInterval(() => {
      const currentBg = messagesListElement.style.backgroundImage;
      const computedBg = window.getComputedStyle(messagesListElement).backgroundImage;
      if (!currentBg || currentBg === 'none' || currentBg === '' ||
        computedBg === 'none' || computedBg === '') {
        applyBackground();
      }
    }, 1000);

    // ✅ CORREÇÃO: Também aplicar quando a janela ganha foco (pode ter sido sobrescrito)
    const handleFocus = () => {
      setTimeout(() => applyBackground(), 100);
    };
    window.addEventListener('focus', handleFocus);

    // ✅ CORREÇÃO: Aplicar quando a visibilidade da página muda
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(() => applyBackground(), 100);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      if (reapplyTimeout) clearTimeout(reapplyTimeout);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [theme.mode, ticketId]);

  useEffect(() => {
    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    };
  }, [dragTimeout]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  }, [loadingMore]);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({});
      }
    }, 100);
  };

  const scrollAndHighlightMessageElement = useCallback((el) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const prevShadow = el.style.boxShadow;
    const prevRadius = el.style.borderRadius;
    const prevTransition = el.style.transition;
    el.style.transition = "box-shadow 0.85s ease-out";
    el.style.boxShadow = "0 0 0 3px rgba(0, 102, 255, 0.45)";
    el.style.borderRadius = "8px";
    window.setTimeout(() => {
      el.style.boxShadow = prevShadow;
      el.style.borderRadius = prevRadius;
      el.style.transition = prevTransition;
    }, 1400);
  }, []);

  /** Clique na citação (resposta) → desliza até a mensagem original no ticket. */
  const scrollToQuotedMessage = useCallback(
    (quotedId, quotedWid) => {
      const widStr =
        quotedWid != null && String(quotedWid).trim() !== "" ? String(quotedWid) : "";
      if ((quotedId == null || quotedId === undefined) && !widStr) return;

      const elForId = (id) => {
        if (id == null || id === undefined) return null;
        return document.getElementById(`ticket-msg-${id}`);
      };

      let el = elForId(quotedId);
      if (!el && messagesList.length > 0) {
        const hit = messagesList.find(
          (m) =>
            (widStr && m.wid && String(m.wid) === widStr) ||
            (quotedId != null && String(m.id) === String(quotedId))
        );
        if (hit?.id != null) {
          el = elForId(hit.id);
        }
      }
      if (!el) return;
      scrollAndHighlightMessageElement(el);
    },
    [messagesList, scrollAndHighlightMessageElement]
  );

  useEffect(() => {
    const handler = (e) => {
      const { messageId, ticketId: evtTicketId } = e.detail || {};
      if (messageId == null || ticketDbId == null) return;
      if (String(evtTicketId) !== String(ticketDbId)) return;
      pendingSearchScrollRef.current = {
        messageId: String(messageId),
        loadsIssued: 0,
        maxLoads: 80,
      };
      setSearchScrollTick((t) => t + 1);
    };
    window.addEventListener("mf-scroll-ticket-message", handler);
    return () => window.removeEventListener("mf-scroll-ticket-message", handler);
  }, [ticketDbId]);

  useEffect(() => {
    const p = pendingSearchScrollRef.current;
    if (!p) return;
    if (loading || loadingMore) return;

    const tryDom = () => {
      const el = document.getElementById(`ticket-msg-${p.messageId}`);
      if (el) {
        scrollAndHighlightMessageElement(el);
        pendingSearchScrollRef.current = null;
        return true;
      }
      return false;
    };

    if (tryDom()) return;

    const inState = messagesList.some((m) => String(m.id) === p.messageId);
    if (inState) {
      const tid = window.setTimeout(() => {
        if (tryDom()) return;
        toast.warning(i18n.t("messagesList.searchScrollNotFound"));
        pendingSearchScrollRef.current = null;
      }, 150);
      return () => window.clearTimeout(tid);
    }

    if (hasMore && p.loadsIssued < p.maxLoads) {
      p.loadsIssued += 1;
      loadMore();
      return;
    }

    toast.warning(i18n.t("messagesList.searchScrollNotFound"));
    pendingSearchScrollRef.current = null;
    return undefined;
  }, [
    messagesList,
    loading,
    loadingMore,
    hasMore,
    searchScrollTick,
    scrollAndHighlightMessageElement,
    loadMore,
  ]);

  const SCROLL_BOTTOM_FAB_THRESHOLD = 120;

  const updateScrollToBottomFab = useCallback((scrollEl) => {
    if (!scrollEl || messagesList.length === 0) {
      setShowScrollToBottomFab(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    if (scrollHeight <= clientHeight + 2) {
      setShowScrollToBottomFab(false);
      return;
    }
    setShowScrollToBottomFab(
      scrollHeight - scrollTop - clientHeight > SCROLL_BOTTOM_FAB_THRESHOLD
    );
  }, [messagesList.length]);

  useLayoutEffect(() => {
    const el = document.getElementById("messagesList");
    if (!el) return;
    updateScrollToBottomFab(el);
    const id = requestAnimationFrame(() => updateScrollToBottomFab(el));
    return () => cancelAnimationFrame(id);
  }, [messagesList.length, ticketId, loading, updateScrollToBottomFab]);

  useEffect(() => {
    if (ticketId === "undefined" || isNil(ticketId)) return;

    const normBody = normMessageBody;

    const onOptimisticAdd = (e) => {
      const d = e.detail;
      if (!d || String(d.ticketDbId) !== String(ticketDbId)) return;
      const { clientTempId, message } = d;
      if (!clientTempId || !message) return;
      if (message.mediaUrl && String(message.mediaUrl).startsWith("blob:")) {
        optimisticBlobUrlsRef.current.set(clientTempId, message.mediaUrl);
      }
      optimisticOrderRef.current.push({
        clientTempId,
        body: normBody(message.body),
        mediaKind: message.mediaKind || null,
      });
      dispatch({ type: "ADD_OPTIMISTIC_MESSAGE", payload: { message } });
      scrollToBottom();
    };

    const onOptimisticError = (e) => {
      const d = e.detail;
      if (!d || String(d.ticketDbId) !== String(ticketDbId)) return;
      const { clientTempId } = d;
      if (!clientTempId) return;
      const url = optimisticBlobUrlsRef.current.get(clientTempId);
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
        optimisticBlobUrlsRef.current.delete(clientTempId);
      }
      optimisticOrderRef.current = optimisticOrderRef.current.filter(
        (x) => x.clientTempId !== clientTempId
      );
      dispatch({ type: "REMOVE_OPTIMISTIC_CLIENT_ID", payload: clientTempId });
    };

    window.addEventListener("multiflow-optimistic-message", onOptimisticAdd);
    window.addEventListener("multiflow-optimistic-message-error", onOptimisticError);
    return () => {
      window.removeEventListener("multiflow-optimistic-message", onOptimisticAdd);
      window.removeEventListener("multiflow-optimistic-message-error", onOptimisticError);
    };
  }, [ticketId, ticketDbId, dispatch]);

  const handleScroll = (e) => {
    updateScrollToBottomFab(e.currentTarget);

    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    // ✅ CORREÇÃO: Capturar posição no momento do clique para evitar bug de
    // menu aparecendo no canto superior esquerdo. O botão tem display:none por
    // padrão e só fica visível no hover — se o mouse sai antes do Menu renderizar,
    // getBoundingClientRect() do anchorEl retorna (0,0) pois o elemento está oculto.
    // Usando anchorPosition, a posição é fixa no momento do clique.
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom, left: rect.right });
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
    setMenuPosition(null);
  };

  const hanldeReplyMessage = (e, message) => {
    setAnchorEl(null);
    setReplyingMessage(message);
  };

  const handleShowReactions = (message, event) => {
    if (showSelectMessageCheckbox) return;
    if (message.fromMe) return;
    if (channel === "whatsapp_oficial") {
      toast.warn(i18n.t("messageOptionsMenu.officialApiNoReactions"), {
        toastId: "officialApiNoReactions",
        autoClose: 4000,
      });
      return;
    }
    if (channel === "facebook" || channel === "instagram") {
      toast.warn(i18n.t("messageOptionsMenu.channelNoReactions"), {
        toastId: "channelNoReactions",
        autoClose: 4000,
      });
      return;
    }
    setMessageWithReactionOpen(message);
    setReactionAnchorEl(event.currentTarget);
  };

  const handleCloseReactions = () => {
    setMessageWithReactionOpen(null);
    setReactionAnchorEl(null);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  const checkMessageMedia = (message) => {
    const isAudioMessage = (message) => {
      const mt = message.mediaType != null ? String(message.mediaType).toLowerCase() : "";
      if (mt === "audio" || mt === "audiomessage" || mt === "ptt" || mt === "voice") {
        return true;
      }

      if (message.mediaUrl) {
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm'];
        const url = message.mediaUrl.toLowerCase();
        const hasAudioExtension = audioExtensions.some(ext => url.includes(ext));

        if (hasAudioExtension) {
          return true;
        }
      }

      if (message.body && typeof message.body === 'string') {
        const body = message.body.toLowerCase();
        const isAudioBody = body.includes('áudio gravado') ||
          body.includes('audio_') ||
          body.includes('🎵') ||
          body.includes('arquivo de áudio') ||
          body.includes('mensagem de voz');

        if (isAudioBody) {
          return true;
        }
      }

      return false;
    };

    // Mensagens com botões ou lista (WhatsApp) – exibir como no app para o cliente identificar
    if (message.mediaType === "buttonsMessage" || message.mediaType === "listMessage" || message.mediaType === "interactiveMessage") {
      return <InteractiveMessagePreview message={message} />;
    }

    // Enquete/Poll - Card visual
    if (message.mediaType === "pollCreationMessage" || message.mediaType === "pollCreationMessageV3" || message.mediaType === "poll" || message.mediaType === "pollMessage") {
      return <PollMessageCard message={message} />;
    }

    // Templates
    if (message.mediaType === "template") {
      const hasButtonsPayload = !!message.body && message.body.includes("||||");

      // Templates com payload estruturado (ex: campanhas, templates salvos)
      // usam o componente específico que entende o formato texto||||JSON(buttons)
      if (hasButtonsPayload) {
        return <Template message={message} />;
      }

      // Template sem "||||" no body mas com mídia gravada (header vídeo/imagem) — exibir header no Template
      if (message.mediaUrl) {
        return <Template message={message} />;
      }

      // Templates simples só texto: bloco Markdown abaixo
      return null;
    }

    // Localização
    else if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|');
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];
      let descriptionLocation = locationParts.length > 2 ? locationParts[2] : null;

      return <LocationPreview
        image={imageLocation}
        link={linkLocation}
        description={descriptionLocation}
      />;
    }

    // Contato único, vários ou encaminhado como texto com vCard no body
    else if (isVcardMessage(message)) {
      const blocks = splitVcardBlocks(message.body);
      const toRender =
        blocks.length > 0
          ? blocks
          : message.body && String(message.body).trim()
            ? [String(message.body).trim()]
            : [];

      if (toRender.length === 0) return null;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "stretch",
          }}
        >
          {toRender.map((block, idx) => {
            const { contact, number } = parseVcardBodyFields(block);
            return (
              <VcardPreview
                key={`vcard-${message.id}-${idx}`}
                contact={contact}
                numbers={number}
                queueId={queueId ?? message?.ticket?.queueId}
                whatsappId={whatsappId ?? message?.ticket?.whatsappId}
                channel={channel}
              />
            );
          })}
        </div>
      );
    }

    else if (message.mediaType === "adMetaPreview") { // Baileys (base64) ou API Oficial CTWA (JSON ou pipes)
      const raw = message.body || "";
      let image;
      let sourceUrl;
      let title;
      let body;
      let messageUser;
      const trimmed = raw.trim();
      let parsedFromJson = false;
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          image = parsed.image;
          sourceUrl = parsed.sourceUrl;
          title = parsed.title;
          body = parsed.body;
          messageUser = parsed.messageUser;
          parsedFromJson = true;
        } catch (_e) {
          parsedFromJson = false;
        }
      }
      if (!parsedFromJson) {
        const parts = raw.split("|");
        image = parts[0];
        sourceUrl = parts[1];
        title = parts[2];
        body = parts[3];
        messageUser = parts.slice(4).join("|");
      }

      if (
        (!messageUser || String(messageUser).trim() === "") &&
        !parsedFromJson
      ) {
        messageUser =
          "Olá! Tenho interesse e queria mais informações, por favor.";
      }

      return (
        <AdMetaPreview
          image={image}
          sourceUrl={sourceUrl}
          title={title}
          body={body}
          messageUser={messageUser}
        />
      );
    }

    else if (message.mediaType === "facebookPostPreview") { // ✅ NOVO: Preview de post do Facebook
      // Formato: image|url|title|body|comment
      let [image, sourceUrl, title, body, comment] = message.body.split('|');

      return <FacebookPostPreview
        image={image}
        sourceUrl={sourceUrl}
        title={title}
        body={body}
        comment={comment}
      />;
    }

    // Áudio (antes de documentos — .ogg não pode cair no card de arquivo)
    else if (isAudioMessage(message)) {

      // Verificar se disco está em 100%
      // Só esconde a mídia se o plano realmente bloqueia acima da cota (checkDiskQuota);
      // sem bloqueio a mídia está no disco e deve aparecer (caso Edapa 10/10/2026).
      const isDiskFull = diskUsage && diskUsage.checkDiskQuota === true && diskUsage.percentage >= 100;

      if (isDiskFull) {
        // Ocultar mídia e mostrar mensagem alternativa
        return (
          <div style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px"
          }}>
            <LockIcon style={{ fontSize: 40, color: "#ff9800", marginBottom: "8px" }} />
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Armazenamento Cheio
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Este áudio não pode ser reproduzido porque o armazenamento está em 100%.
            </Typography>
            {message.mediaName && (
              <Typography variant="caption" style={{ display: "block", marginTop: "8px", fontStyle: "italic" }}>
                Arquivo: {message.mediaName}
              </Typography>
            )}
          </div>
        );
      }

      return (
        <div style={{ width: "100%", maxWidth: 320, padding: 0 }}>
          <AudioModal
            url={message.mediaUrl}
            message={message}
            senderUser={message.fromMe ? resolveMessageOperatorUser(message, user) : null}
            companyId={companyId}
          />
        </div>
      );
    }

    // PDF e documentos (cdr, xlsx, txt, etc.) — card com nome e extensão
    else if (
      isPdfUrl(message.mediaUrl, message.body, message.mediaType) ||
      isAttachmentDocumentMessage(message)
    ) {

      // Verificar se disco está em 100%
      // Só esconde a mídia se o plano realmente bloqueia acima da cota (checkDiskQuota);
      // sem bloqueio a mídia está no disco e deve aparecer (caso Edapa 10/10/2026).
      const isDiskFull = diskUsage && diskUsage.checkDiskQuota === true && diskUsage.percentage >= 100;

      if (isDiskFull) {
        // Ocultar mídia e mostrar mensagem alternativa
        return (
          <div style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px"
          }}>
            <LockIcon style={{ fontSize: 40, color: "#ff9800", marginBottom: "8px" }} />
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Armazenamento Cheio
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Este documento não pode ser exibido porque o armazenamento está em 100%.
            </Typography>
            {message.body && (
              <Typography variant="caption" style={{ display: "block", marginTop: "8px", fontStyle: "italic" }}>
                Arquivo: {message.body}
              </Typography>
            )}
          </div>
        );
      }

      // ✅ CORRIGIDO: Passar companyId para construir a URL correta
      const pdfInfo = extractPdfInfoFromMessage(message, companyId);

      return (
        <PdfPreview
          url={pdfInfo.url}
          filename={pdfInfo.filename}
          size={pdfInfo.size}
          mediaType={pdfInfo.mediaType}
          onDownload={(url, name) => {
            downloadPdf(url, name);
          }}
        />
      );
    }

    // Imagens
    else if (isImageMediaType(message.mediaType)) {

      // Verificar se disco está em 100%
      // Só esconde a mídia se o plano realmente bloqueia acima da cota (checkDiskQuota);
      // sem bloqueio a mídia está no disco e deve aparecer (caso Edapa 10/10/2026).
      const isDiskFull = diskUsage && diskUsage.checkDiskQuota === true && diskUsage.percentage >= 100;

      if (isDiskFull) {
        // Ocultar mídia e mostrar mensagem alternativa
        return (
          <div style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px"
          }}>
            <LockIcon style={{ fontSize: 40, color: "#ff9800", marginBottom: "8px" }} />
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Armazenamento Cheio
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Esta mídia não pode ser exibida porque o armazenamento está em 100%.
            </Typography>
            {message.mediaName && (
              <Typography variant="caption" style={{ display: "block", marginTop: "8px", fontStyle: "italic" }}>
                Arquivo: {message.mediaName}
              </Typography>
            )}
          </div>
        );
      }

      // ✅ CORRIGIDO: O mediaUrl já vem do backend com a URL completa construída pelo modelo Message
      // O modelo Message constrói: ${BACKEND_URL}/public/company${companyId}/${mediaUrl}
      // Se mediaUrl = "flow/logo-swl-plus.png", a URL final será: ${BACKEND_URL}/public/company1/flow/logo-swl-plus.png
      // Então não precisamos fazer nenhuma transformação aqui, apenas usar o mediaUrl diretamente
      const imageUrl = message.mediaUrl || '';
      return <ModalImageCors imageUrl={imageUrl} />;
    }

    // Vídeos
    else if (isVideoMediaType(message.mediaType)) {

      // Verificar se disco está em 100%
      // Só esconde a mídia se o plano realmente bloqueia acima da cota (checkDiskQuota);
      // sem bloqueio a mídia está no disco e deve aparecer (caso Edapa 10/10/2026).
      const isDiskFull = diskUsage && diskUsage.checkDiskQuota === true && diskUsage.percentage >= 100;

      if (isDiskFull) {
        // Ocultar mídia e mostrar mensagem alternativa
        return (
          <div style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px"
          }}>
            <LockIcon style={{ fontSize: 40, color: "#ff9800", marginBottom: "8px" }} />
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Armazenamento Cheio
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Este vídeo não pode ser exibido porque o armazenamento está em 100%.
            </Typography>
            {message.mediaName && (
              <Typography variant="caption" style={{ display: "block", marginTop: "8px", fontStyle: "italic" }}>
                Arquivo: {message.mediaName}
              </Typography>
            )}
          </div>
        );
      }

      return (
        <div style={{ maxWidth: "400px", width: "100%", position: "relative" }}>
          {/* Loading indicator */}
          {videoLoading && !videoError && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px"
            }}>
              <CircularProgress size={30} />
              <Typography variant="caption" color="textSecondary">
                Carregando vídeo...
              </Typography>
            </div>
          )}

          {/* Vídeo player melhorado */}
          <video
            className={classes.messageMedia}
            src={message.mediaUrl}
            controls
            preload="metadata"
            playsInline
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "300px",
              borderRadius: "8px",
              backgroundColor: "#f0f0f0",
              opacity: videoLoading ? 0.3 : 1,
              transition: "opacity 0.3s ease"
            }}
            onLoadStart={() => {
              setVideoLoading(true);
              setVideoError(false);
            }}
            onLoadedData={() => {
              setVideoLoading(false);
            }}
            onCanPlay={() => {
              setVideoLoading(false);
            }}
            onError={(e) => {
              // ✅ Prevenir que o erro seja propagado para o handler global
              e.stopPropagation();

              const videoElement = e.target;
              const error = videoElement.error;
              const videoUrl = message.mediaUrl;

              // ✅ Evitar logs duplicados - verificar se já logamos este erro
              const errorKey = `${videoUrl}_${error?.code || 'unknown'}`;
              if (videoErrorLogCache.current.has(errorKey)) {
                // Já logamos este erro, apenas atualizar o estado
                setVideoLoading(false);
                setVideoError(true);
                return;
              }

              // Marcar como logado
              videoErrorLogCache.current.add(errorKey);

              if (error) {
                let errorMessage = "Erro desconhecido ao carregar vídeo";

                switch (error.code) {
                  case error.MEDIA_ERR_ABORTED:
                    errorMessage = "O carregamento do vídeo foi abortado";
                    break;
                  case error.MEDIA_ERR_NETWORK:
                    errorMessage = "Erro de rede ao carregar o vídeo";
                    break;
                  case error.MEDIA_ERR_DECODE:
                    errorMessage = "Erro ao decodificar o vídeo";
                    break;
                  case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    errorMessage = "Formato de vídeo não suportado";
                    break;
                  default:
                    break;
                }

                // ✅ Log apenas em modo debug, não como erro crítico, e apenas uma vez
                if (process.env.NODE_ENV === 'development') {
                  console.warn("⚠️ Erro ao carregar vídeo:", {
                    message: errorMessage,
                    code: error.code,
                    url: videoUrl
                  });
                }
              } else {
                // ✅ Log apenas em modo debug
                if (process.env.NODE_ENV === 'development') {
                  console.warn("⚠️ Erro ao carregar vídeo (sem detalhes):", videoUrl);
                }
              }

              setVideoLoading(false);
              setVideoError(true);
            }}
          >
            {/* ✅ CORREÇÃO: Múltiplos formatos para compatibilidade */}
            <source src={message.mediaUrl} type="video/mp4" />
            <source src={message.mediaUrl} type="video/webm" />
            <source src={message.mediaUrl} type="video/ogg" />

            {/* Fallback para navegadores antigos */}
            Seu navegador não suporta reprodução de vídeo.
          </video>

          {/* Error state */}
          {videoError && (
            <div style={{
              padding: "20px",
              textAlign: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              color: "#666",
              marginTop: "8px"
            }}>
              <Typography variant="body2" style={{ marginBottom: "12px" }}>
                ❌ Erro ao carregar vídeo
              </Typography>
              <Button
                startIcon={<GetApp />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = message.mediaUrl;
                  link.download = message.body || 'video.mp4';
                  link.click();
                }}
                variant="outlined"
                size="small"
              >
                Baixar Vídeo
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Outros tipos de arquivo
    else if (message.mediaUrl) {

      // Verificar se disco está em 100%
      // Só esconde a mídia se o plano realmente bloqueia acima da cota (checkDiskQuota);
      // sem bloqueio a mídia está no disco e deve aparecer (caso Edapa 10/10/2026).
      const isDiskFull = diskUsage && diskUsage.checkDiskQuota === true && diskUsage.percentage >= 100;

      if (isDiskFull) {
        // Ocultar botão de download e mostrar mensagem alternativa
        return (
          <div style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px"
          }}>
            <LockIcon style={{ fontSize: 40, color: "#ff9800", marginBottom: "8px" }} />
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Armazenamento Cheio
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Este arquivo não pode ser baixado porque o armazenamento está em 100%.
            </Typography>
            {message.body && (
              <Typography variant="caption" style={{ display: "block", marginTop: "8px", fontStyle: "italic" }}>
                Arquivo: {message.body}
              </Typography>
            )}
          </div>
        );
      }

      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              variant="outlined"
              onClick={() =>
                downloadPdf(
                  message.mediaUrl,
                  getDocumentDownloadFilename(message, "arquivo")
                )
              }
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }

    return null;
  };

  /** Tooltip do ícone de falha: usa metaSendError em dataJson (webhook failed) quando existir. */
  const parseMessageDataJson = (message) => {
    if (!message?.dataJson) return null;
    try {
      return typeof message.dataJson === "string"
        ? JSON.parse(message.dataJson)
        : message.dataJson;
    } catch (e) {
      return null;
    }
  };

  const getBaileysSendError = (message) => {
    const parsed = parseMessageDataJson(message);
    return parsed?.metaSendError || parsed?.baileysSendError || null;
  };

  const isAccountRestrictedSendError = (err) =>
    err &&
    (err.kind === "ACCOUNT_RESTRICTED" ||
      String(err.code) === "463" ||
      Number(err.code) === 463);

  const getFailedSendTooltip = (message) => {
    const err = getBaileysSendError(message);
    if (isAccountRestrictedSendError(err)) {
      const name = err.connectionName || message?.ticket?.whatsapp?.name;
      if (name) {
        return i18n.t("messagesList.baileysAccountRestricted", { name });
      }
      return err.message || i18n.t("messagesList.baileysAccountRestrictedGeneric");
    }
    if (err && (err.code != null || err.title || err.message || err.details)) {
      const codePrefix = err.code != null ? `(#${err.code}) ` : "";
      const text = (err.details || err.title || err.message || "").trim();
      if (text) return `${codePrefix}${text}`;
      return codePrefix.trim() || i18n.t("messagesList.messageFailed");
    }
    return i18n.t("messagesList.messageFailed");
  };

  const getFailedSendHint = (message) => {
    if (message.ack !== -1) return null;
    const err = getBaileysSendError(message);
    if (!isAccountRestrictedSendError(err)) return null;
    return getFailedSendTooltip(message);
  };

  const renderMessageAck = (message) => {
    if (message.ack === -1) {
      return (
        <Tooltip
          title={getFailedSendTooltip(message)}
          placement="top"
          arrow
        >
          <ErrorIcon fontSize="small" className={classes.ackErrorIcon} />
        </Tooltip>
      );
    } else if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 2) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 4) {
      const isAudioAck =
        message.mediaType === "audio" ||
        message.mediaType === "audioMessage" ||
        message.mediaType === "ptt";
      return <DoneAll fontSize="small" className={isAudioAck ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
    } else if (message.ack === 5) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
    }
  };

  const renderDailyTimestamps = (message, index, list = messagesList) => {
    const today = format(new Date(), "dd/MM/yyyy")

    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {today === format(parseISO(list[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(list[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    } else
      if (index < list.length - 1) {
        let messageDay = parseISO(list[index].createdAt);
        let previousMessageDay = parseISO(list[index - 1].createdAt);

        if (!isSameDay(messageDay, previousMessageDay)) {
          return (
            <span
              className={classes.dailyTimestamp}
              key={`timestamp-${message.id}`}
            >
              <div className={classes.dailyTimestampText}>
                {today === format(parseISO(list[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(list[index].createdAt), "dd/MM/yyyy")}
              </div>
            </span>
          );
        }
      } else
        if (index === list.length - 1) {
          return (
            <div
              key={`ref-${message.id}`}
              ref={lastMessageRef}
              style={{ float: "left", clear: "both" }}
            />
          );
        }
  };

  // ✅ Função para buscar fila pelo ID quando necessário
  const fetchQueueById = useCallback(async (queueId) => {
    if (!queueId || queuesCache.has(queueId)) {
      return queuesCache.get(queueId);
    }

    try {
      const { data } = await api.get(`/queue/${queueId}`);
      if (data) {
        setQueuesCache(prev => new Map(prev).set(queueId, data));
        return data;
      }
    } catch (error) {
      // Se não encontrar, pode ter sido deletada
      console.warn(`[MessagesList] Fila ${queueId} não encontrada`);
    }
    return null;
  }, [queuesCache]);

  const renderTicketsSeparator = (message, index, list = messagesList) => {
    let lastTicket = list[index - 1]?.ticketId;
    let currentTicket = message.ticketId;

    if (lastTicket !== currentTicket && lastTicket !== undefined) {
      // ✅ CORREÇÃO CRÍTICA: Cada mensagem deve manter a fila do ticket ao qual pertence originalmente
      // NUNCA usar a fila do ticket atual - isso substituiria o histórico
      // Prioridade: 1) queueId da mensagem, 2) queue do ticket da mensagem, 3) queueId do ticket da mensagem
      const messageQueue = message?.queue; // Fila da própria mensagem (preservada do histórico)
      const messageQueueId = message?.queueId; // queueId da própria mensagem
      const messageTicket = message?.ticket; // Ticket ao qual a mensagem pertence
      const ticketQueue = messageTicket?.queue; // Fila do ticket ao qual a mensagem pertence
      const ticketQueueId = messageTicket?.queueId; // queueId do ticket ao qual a mensagem pertence

      // ✅ Usar fila da mensagem primeiro (preserva histórico), depois do ticket da mensagem
      let queue = messageQueue || ticketQueue;
      const queueId = messageQueueId || ticketQueueId;

      // ✅ Se temos queueId mas não temos a fila, tentar buscar do cache
      if (!queue && queueId && queuesCache.has(queueId)) {
        queue = queuesCache.get(queueId);
      }

      if (queue && queue.name) {
        // ✅ Fila carregada corretamente - mostrar nome e cor
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}a`}
          >
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: queue.color || "grey" }}
            >
              #{i18n.t("ticketsList.called")} {message?.ticketId} - {queue.name}
            </div>

          </span>
        );
      } else if (queueId && queueId !== null) {
        // ✅ Tem queueId mas relação não foi carregada - buscar assincronamente
        // Por enquanto mostrar com ID, mas iniciar busca
        if (!queuesCache.has(queueId)) {
          fetchQueueById(queueId).then(fetchedQueue => {
            if (fetchedQueue) {
              // Forçar re-render atualizando o estado
              setQueuesCache(prev => new Map(prev));
            }
          });
        }

        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}c`}
          >
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: "grey" }}
            >
              #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")} (ID: {queueId})
            </div>

          </span>
        );
      } else {
        // ✅ Realmente não tem fila
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}b`}
          >
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: "grey" }}
            >
              #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
            </div>

          </span>
        );
      }
    }

  };

  const renderMessageDivider = (message, index, list = messagesList) => {
    if (index < list.length && index > 0) {
      let messageUser = list[index].fromMe;
      let previousMessageUser = list[index - 1].fromMe;
      if (messageUser !== previousMessageUser) {
        return (

          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    const q = message.quotedMsg;
    if (!q) return null;
    const mt = q.mediaType;
    const mediaUrl = q.mediaUrl;
    const showAudio = quotedMediaIsAudio(mt);
    const showVideo = quotedMediaIsVideo(mt);
    const showImage = quotedMediaIsImage(mt, mediaUrl);
    const showDoc = quotedMediaIsDocument(mt);
    const showContact = quotedMediaIsContact(mt);
    const hasMediaPreview = !!(showAudio || showVideo || showImage || showDoc || showContact);
    const quotedDbId = q.id;
    const quotedWid = q.wid;
    const canScrollToOriginal =
      quotedDbId != null || (quotedWid != null && String(quotedWid).trim() !== "");

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
          [classes.quotedMessageClickable]: canScrollToOriginal,
        })}
        onClick={
          canScrollToOriginal
            ? (e) => {
                e.stopPropagation();
                const t = e.target;
                if (typeof t.closest === "function") {
                  if (t.closest("button, a")) return;
                  if (t.closest("video, audio")) return;
                  if (
                    t.closest("[class*='react-modal-image']") ||
                    t.closest("[class*='__react_modal_image']")
                  ) {
                    return;
                  }
                }
                scrollToQuotedMessage(quotedDbId, quotedWid);
              }
            : undefined
        }
        onKeyDown={
          canScrollToOriginal
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToQuotedMessage(quotedDbId, quotedWid);
                }
              }
            : undefined
        }
        role={canScrollToOriginal ? "button" : undefined}
        tabIndex={canScrollToOriginal ? 0 : undefined}
        title={canScrollToOriginal ? i18n.t("messagesList.scrollToQuoted") : undefined}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: q.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {q.fromMe ? (
            <span className={classes.messageContactName} style={{ color: "#6bcbef" }}>Você</span>
          ) : (
            <span className={classes.messageContactName} style={{ color: getContactColor(q.contact?.id || q.contact?.number) }}>
              {q.contact?.name || ""}
            </span>
          )}

          {showAudio && mediaUrl && (
            <div className={classes.downloadMedia}>
              <AudioModal url={mediaUrl} />
            </div>
          )}
          {showVideo && mediaUrl && (
            <div style={{ maxWidth: "300px", width: "100%" }}>
              <video
                className={classes.messageMedia}
                src={mediaUrl}
                controls
                preload="metadata"
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "200px",
                  borderRadius: "6px",
                  backgroundColor: "#f0f0f0"
                }}
                onError={(e) => {
                  console.error("❌ Erro ao carregar vídeo citado:", e);
                }}
              >
                <source src={mediaUrl} type="video/mp4" />
                <source src={mediaUrl} type="video/webm" />
                <source src={mediaUrl} type="video/ogg" />
                <div style={{ padding: "10px", textAlign: "center", fontSize: "12px", color: "#999" }}>
                  ❌ Erro ao carregar vídeo
                </div>
              </video>
            </div>
          )}
          {showContact && "Contato"}
          {showDoc && mediaUrl && (
            <PdfPreview
              url={mediaUrl}
              filename={getDocumentDownloadFilename(q, "documento")}
              mediaType={q.mediaType}
              onDownload={(url, name) => {
                downloadPdf(url, name);
              }}
            />
          )}

          {showImage && mediaUrl && (
            <ModalImageCors imageUrl={mediaUrl} variant="quoted" />
          )}

          {q.body && String(q.body).trim() !== "" && (
            <div
              style={{
                marginTop: hasMediaPreview ? 6 : 0,
                fontSize: "0.9em",
                opacity: 0.95,
                wordBreak: "break-word",
              }}
            >
              {q.body}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      const hasFiles = event.dataTransfer &&
        event.dataTransfer.types &&
        (event.dataTransfer.types.includes('Files') ||
          event.dataTransfer.types.includes('application/x-moz-file'));

      if (hasFiles) {
        if (dragTimeout) {
          clearTimeout(dragTimeout);
        }

        // Capturar dataTransfer antes do setTimeout para evitar problemas de acesso
        const dataTransfer = event.dataTransfer;

        const timeout = setTimeout(() => {
          // Verificar se dataTransfer ainda existe e tem items antes de acessar
          if (dataTransfer && dataTransfer.items && dataTransfer.items.length > 0) {
            setDragActive(true);
          }
        }, 100);

        setDragTimeout(timeout);
      }
    } else if (event.type === "dragleave") {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  }

  const isYouTubeLink = (url) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();

    if (dragTimeout) {
      clearTimeout(dragTimeout);
      setDragTimeout(null);
    }

    setDragActive(false);

    // Verificar se dataTransfer existe e tem files antes de acessar
    if (event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files.length > 0 &&
      event.dataTransfer.files[0] instanceof File) {
      if (onDrop) {
        onDrop(event.dataTransfer.files);
      }
    }
  }
  const xmlRegex = /<([^>]+)>/g;
  const boldRegex = /\*(.*?)\*/g;

  const formatXml = (xmlString) => {
    // Verifica se o XML contém a assinatura com nome do atendente
    if (boldRegex.test(xmlString)) {
      // Formata o texto dentro da assinatura em negrito
      xmlString = xmlString.replace(boldRegex, "**$1**");
    }
    return xmlString;
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      // Evitar duplicata real (mesmo envio + eco WhatsApp): só quando wid ou id repetido.
      // NÃO usar body+HH:mm — várias mensagens legítimas no mesmo minuto com o mesmo texto sumiam do chat.
      const MEDIA_TYPES = [
        "audio",
        "image",
        "video",
        "document",
        "application",
        "sticker",
        "audioMessage",
        "ptt",
        "imageMessage",
        "videoMessage",
        "documentMessage",
        "stickerMessage",
        "pttMessage",
      ];
      const isAttachmentMessage = (msg) => !!(msg.mediaUrl || (msg.mediaType && MEDIA_TYPES.includes(msg.mediaType)));
      const normBodyKey = (b) => String(b || "").replace(/\r\n/g, "\n").trim();
      const seenOurWid = new Set();
      const seenOurId = new Set();
      const listToRender = messagesList.filter((message) => {
        if (!message.fromMe) return true;
        if (isAttachmentMessage(message)) return true;
        if (!normBodyKey(message.body) && !message.optimistic) return false;
        if (message.optimistic) {
          const hasRealTwin = messagesList.some(
            (m) =>
              !m.optimistic &&
              m.fromMe &&
              !isAttachmentMessage(m) &&
              normBodyKey(m.body) === normBodyKey(message.body)
          );
          if (hasRealTwin) return false;
        }
        if (message.id != null && seenOurId.has(message.id)) return false;
        if (message.id != null) seenOurId.add(message.id);
        if (message.wid) {
          if (seenOurWid.has(message.wid)) return false;
          seenOurWid.add(message.wid);
        }
        return true;
      });

      const viewMessagesList = listToRender.map((message, index) => {
        // ── Agrupamento de imagens consecutivas ──
        const groupInfo = imageGroupMap.get(String(message.id));
        if (groupInfo?.isGrouped && !groupInfo.isFirst) return null; // ocultar mensagens agrupadas (não-primeira)
        if (groupInfo?.isFirst) {
          return (
            <React.Fragment key={`img-group-${message.id}`}>
              {renderDailyTimestamps(message, index, listToRender)}
              {renderTicketsSeparator(message, index, listToRender)}
              {renderMessageDivider(message, index, listToRender)}
              {message.fromMe ? (
                <div className={classes.messageRightRow}>
                  <div
                    id={`ticket-msg-${message.id}`}
                    className={clsx(classes.messageRight, classes.messageRightInRow)}
                  >
                    <ImageGrid messages={groupInfo.messages} fromMe={message.fromMe} />
                  </div>
                  <MessageOperatorAvatar message={message} companyId={companyId} />
                </div>
              ) : (
                <div
                  id={`ticket-msg-${message.id}`}
                  className={classes.messageLeft}
                >
                  {isGroup && !message.fromMe && (
                    <span
                      className={classes.messageContactName}
                      style={{
                        color: getContactColor(
                          message.contact?.id || message.contact?.number
                        ),
                      }}
                    >
                      {message.contact?.name || message.contact?.number || ""}
                    </span>
                  )}
                  <ImageGrid messages={groupInfo.messages} fromMe={message.fromMe} />
                </div>
              )}
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index, listToRender)}
              {renderTicketsSeparator(message, index, listToRender)}
              {renderMessageDivider(message, index, listToRender)}
              <div id={`ticket-msg-${message.id}`} className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName} style={{ color: getContactColor(message.contact?.id || message.contact?.number) }}>
                    {message.contact?.name || message.contact?.number || ""}
                  </span>
                )}

                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                    <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                  </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index, listToRender)}
              {renderTicketsSeparator(message, index, listToRender)}
              {renderMessageDivider(message, index, listToRender)}
              <div
                id={`ticket-msg-${message.id}`}
                className={clsx(classes.messageLeft, {
                  [classes.messageWrapperWithReaction]: message.reaction
                })}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>

                {!showSelectMessageCheckbox &&
                  !message.fromMe && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleShowReactions(message, e)}
                      className={classes.emojiButtonLeft}
                    >
                      <SentimentSatisfiedAlt fontSize="small" />
                    </IconButton>
                  )}

                {messageWithReactionOpen &&
                  messageWithReactionOpen.id === message.id && (
                    <MessageReactions
                      message={message}
                      onClose={handleCloseReactions}
                      ticketId={ticketId}
                      channel={channel}
                      anchorEl={reactionAnchorEl}
                    />
                  )}

                {message.reaction && !showSelectMessageCheckbox && (
                  <Tooltip
                    title={
                      message.fromMe
                        ? "Reação recebida"
                        : "Você reagiu a esta mensagem"
                    }
                    placement="bottom"
                  >
                    <div className={message.fromMe ? classes.reactionBubbleRight : classes.reactionBubble}>
                      <span>{message.reaction}</span>
                    </div>
                  </Tooltip>
                )}

                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && (
                  <span className={classes.messageContactName} style={{ color: getContactColor(message.contact?.id || message.contact?.number) }}>
                    {message.contact?.name || message.contact?.number || ""}
                  </span>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}

                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}
                    >🚫 Essa mensagem foi apagada pelo contato &nbsp;
                    </span>
                  </div>
                )}

                {(message.mediaUrl || message.mediaType === "locationMessage" || isVcardMessage(message) || message.mediaType === "template" || message.mediaType === "adMetaPreview" || message.mediaType === "facebookPostPreview" || message.mediaType === "buttonsMessage" || message.mediaType === "listMessage" || message.mediaType === "interactiveMessage" || message.mediaType === "pollCreationMessage" || message.mediaType === "pollCreationMessageV3"
                ) && checkMessageMedia(message)}

                <div className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}>
                  {/* ✅ CORREÇÃO: Para reações, sempre mostrar o quotedMsg (mensagem original) */}
                  {message.quotedMsg && message.mediaType === "reactionMessage" && renderQuotedMessage(message)}
                  {/* Para outras mensagens com quotedMsg, mostrar normalmente */}
                  {message.quotedMsg && message.mediaType !== "reactionMessage" && renderQuotedMessage(message)}

                  {/* ✅ CORREÇÃO: Renderizar templates simples (sem "||||") explicitamente — com mediaUrl o <Template> já exibe o texto */}
                  {message.mediaType === "template" && !(message.body && message.body.includes("||||")) && !message.mediaUrl && (
                    <>
                      {xmlRegex.test(message.body) && (
                        <span>{message.body}</span>
                      )}
                      {!xmlRegex.test(message.body) && (
                        <MarkdownWrapper>
                          {(lgpdDeleteMessage && message.isDeleted) 
                            ? "🚫 _Mensagem apagada_ " 
                            : (message.body && message.body.trim() !== "")
                              ? message.body
                              : "📋 Template enviado"}
                        </MarkdownWrapper>
                      )}
                    </>
                  )}
                  {/* Renderizar outros tipos de mensagem (exceto templates simples e botão/lista) */}
                  {
                    message.mediaType !== "template" &&
                    message.mediaType !== "adMetaPreview" && 
                    message.mediaType !== "facebookPostPreview" &&
                    message.mediaType !== "buttonsMessage" &&
                    message.mediaType !== "listMessage" &&
                    message.mediaType !== "interactiveMessage" && message.mediaType !== "pollCreationMessage" && message.mediaType !== "pollCreationMessageV3" && (
                      (message.mediaUrl !== null && (isImageMediaType(message.mediaType) || isVideoMediaType(message.mediaType)) && getBasename(message.mediaUrl).trim() !== message.body.trim()) ||
                      (message.mediaType !== "audio" &&
                      message.mediaType !== "audioMessage" &&
                      message.mediaType !== "ptt" &&
                      message.mediaType !== "image" &&
                      message.mediaType !== "video" &&
                      message.mediaType !== "reactionMessage" &&
                      message.mediaType !== "locationMessage" &&
                      !isVcardMessage(message))
                    ) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <span>{message.body}</span>
                        )}
                        {!xmlRegex.test(message.body) && (
                          <MarkdownWrapper>
                            {(lgpdDeleteMessage && message.isDeleted) ? "🚫 _Mensagem apagada_ " : message.body}
                          </MarkdownWrapper>
                        )}
                      </>
                    )
                  }

                  {/* ✅ CORREÇÃO: Mostrar emoji da reação junto com o texto */}
                  {message.quotedMsg && message.mediaType === "reactionMessage" && (
                    <>
                      <span style={{ marginLeft: "8px", fontSize: "1.2em", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <span>{message?.contact?.name || "Você"}</span>
                        <span>reagiu com</span>
                        <span style={{ fontSize: "1.5em" }}>{message.body || "👍"}</span>
                      </span>
                    </>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          const failedSendHint = getFailedSendHint(message);
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index, listToRender)}
              {renderTicketsSeparator(message, index, listToRender)}
              {renderMessageDivider(message, index, listToRender)}
              <div className={classes.messageRightRow}>
              <div
                id={`ticket-msg-${message.id}`}
                className={clsx(getOutgoingBubbleClass(message, classes), classes.messageRightInRow, {
                  [classes.messageWrapperWithReaction]: message.reaction
                })}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}

                {(() => {
                  const origin = resolveAutomatedOrigin(message);
                  if (!origin) return null;
                  const meta = AUTOMATED_ORIGIN_META[origin] || AUTOMATED_ORIGIN_META.campaign;
                  const OriginIcon = meta.Icon;
                  return (
                    <Tooltip title={meta.label} placement="top">
                      <span className={classes.automatedOriginBadge}>
                        <OriginIcon style={{ fontSize: 14 }} />
                      </span>
                    </Tooltip>
                  );
                })()}

                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>

                {!showSelectMessageCheckbox &&
                  !message.fromMe && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleShowReactions(message, e)}
                      className={classes.emojiButtonLeft}
                    >
                      <SentimentSatisfiedAlt fontSize="small" />
                    </IconButton>
                  )}

                {messageWithReactionOpen &&
                  messageWithReactionOpen.id === message.id && (
                    <MessageReactions
                      message={message}
                      onClose={handleCloseReactions}
                      ticketId={ticketId}
                      channel={channel}
                      anchorEl={reactionAnchorEl}
                    />
                  )}

                {message.reaction && !showSelectMessageCheckbox && (
                  <Tooltip
                    title={
                      message.fromMe
                        ? "Reação recebida"
                        : "Você reagiu a esta mensagem"
                    }
                    placement="bottom"
                  >
                    <div className={message.fromMe ? classes.reactionBubbleRight : classes.reactionBubble}>
                      <span>{message.reaction}</span>
                    </div>
                  </Tooltip>
                )}

                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}
                {/* Apagada no destino: admin ainda vê o texto original abaixo do rótulo */}
                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage} style={{ fontSize: "0.85em", fontStyle: "italic" }}>
                      Mensagem Apagada
                    </span>
                  </div>
                )}
                {(message.mediaUrl || message.mediaType === "locationMessage" || isVcardMessage(message) || message.mediaType === "template" || message.mediaType === "adMetaPreview" || message.mediaType === "facebookPostPreview" || message.mediaType === "buttonsMessage" || message.mediaType === "listMessage" || message.mediaType === "interactiveMessage" || message.mediaType === "pollCreationMessage" || message.mediaType === "pollCreationMessageV3"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted && !message.fromMe,
                  })}
                >

                  {/* ✅ CORREÇÃO: Para reações, sempre mostrar o quotedMsg (mensagem original) */}
                  {message.quotedMsg && message.mediaType === "reactionMessage" && renderQuotedMessage(message)}
                  {/* Para outras mensagens com quotedMsg, mostrar normalmente */}
                  {message.quotedMsg && message.mediaType !== "reactionMessage" && renderQuotedMessage(message)}

                  {/* ✅ CORREÇÃO: Templates simples; com mediaUrl o <Template> já exibe o texto */}
                  {message.mediaType === "template" && !(message.body && message.body.includes("||||")) && !message.mediaUrl && (
                    <>
                      {xmlRegex.test(message.body) && (
                        <div>{formatXml(message.body)}</div>
                      )}
                      {!xmlRegex.test(message.body) && (
                        <MarkdownWrapper>
                          {(message.body && message.body.trim() !== "")
                            ? message.body
                            : "📋 Template enviado"}
                        </MarkdownWrapper>
                      )}
                    </>
                  )}
                  {/* Renderizar outros tipos de mensagem (exceto templates simples e botão/lista) */}
                  {
                    message.mediaType !== "template" &&
                    message.mediaType !== "buttonsMessage" &&
                    message.mediaType !== "listMessage" &&
                    message.mediaType !== "interactiveMessage" && message.mediaType !== "pollCreationMessage" && message.mediaType !== "pollCreationMessageV3" && (
                      ((isImageMediaType(message.mediaType) || isVideoMediaType(message.mediaType)) && getBasename(message.mediaUrl) === message.body) ||
                      (message.mediaType !== "audio" && message.mediaType !== "audioMessage" && message.mediaType !== "ptt" && message.mediaType !== "reactionMessage" && message.mediaType !== "locationMessage" && !isVcardMessage(message) && message.mediaType !== "adMetaPreview" && message.mediaType !== "facebookPostPreview")
                    ) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <div>{formatXml(message.body)}</div>
                        )}
                        {!xmlRegex.test(message.body) && (
                          <MarkdownWrapper>
                            {(lgpdDeleteMessage && message.isDeleted)
                              ? "🚫 _Mensagem apagada_ "
                              : message.body}
                          </MarkdownWrapper>
                        )}
                      </>
                    )
                  }

                  {/* ✅ CORREÇÃO: Mostrar emoji da reação junto com o texto */}
                  {message.quotedMsg && message.mediaType === "reactionMessage" && (
                    <>
                      <span style={{ marginLeft: "8px", fontSize: "1.2em", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <span>Você reagiu com</span>
                        <span style={{ fontSize: "1.5em" }}>{message.body || "👍"}</span>
                      </span>
                    </>
                  )}

                  {failedSendHint && (
                    <span className={classes.deliveryFailHint}>
                      {failedSendHint}
                    </span>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
              {!isAudioMessageType(message) && (
                <MessageOperatorAvatar message={message} companyId={companyId} />
              )}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Diga olá para seu novo contato!</div>;
    }
  };
  const shouldBlurMessages = ticketStatus === "pending" && user.allowSeeMessagesInPendingTickets === "disabled";

  return (
    <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
      {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>Solte o arquivo aqui</div>}
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        menuPosition={menuPosition}
        handleClose={handleCloseMessageOptionsMenu}
        isGroup={isGroup}
        whatsappId={whatsappId}
        queueId={queueId}
        channel={channel}
      />

      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
        style={{
          filter: shouldBlurMessages ? "blur(4px)" : "none",
          pointerEvents: shouldBlurMessages ? "none" : "auto",
          // ✅ CORREÇÃO CRÍTICA: Garantir que o backgroundImage esteja sempre presente no estilo inline
          backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
          backgroundRepeat: "repeat",
          backgroundSize: "auto",
          backgroundPosition: "0 0",
          backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
          // ✅ CORREÇÃO: Garantir que o background não seja sobrescrito
          backgroundAttachment: "fixed",
          // ✅ CORREÇÃO: Forçar aplicação do background
          backgroundClip: "border-box",
          backgroundOrigin: "padding-box"
        }}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>

      {showScrollToBottomFab && !shouldBlurMessages && (
        <Tooltip title={i18n.t("messagesList.scrollToBottom")}>
          <Fab
            size="small"
            color="primary"
            className={classes.scrollToBottomFab}
            aria-label={i18n.t("messagesList.scrollToBottom")}
            onClick={() => scrollToBottom()}
          >
            <KeyboardArrowDown />
          </Fab>
        </Tooltip>
      )}

      {(META_CHANNELS.includes(channel)) && (
        <div
          style={{
            width: "100%",
            display: "flex",
            padding: "12px 16px",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            backgroundColor: "#E1F3FB",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", flex: 1, minWidth: 0 }}>
            {channel === "facebook" ? (
              <Facebook style={{ color: "#1877F2", fontSize: 24, flexShrink: 0 }} />
            ) : channel === "instagram" ? (
              <Instagram style={{ fontSize: 24, flexShrink: 0 }} />
            ) : (
              <WhatsApp style={{ color: "#25D366", fontSize: 24, flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: policyTextExpanded ? "6px" : 0,
                  cursor: isMobile ? "pointer" : "default",
                }}
                onClick={() => isMobile && setPolicyTextExpanded((e) => !e)}
                role={isMobile ? "button" : undefined}
                aria-expanded={policyTextExpanded}
                aria-label={isMobile ? (policyTextExpanded ? "Ocultar texto da política" : "Ver texto da política") : undefined}
              >
                <AccessTime style={{ fontSize: 18, color: "#555" }} />
                <strong style={{ fontSize: "14px" }}>Política de Mensagens</strong>
                {isMobile && (
                  <IconButton
                    size="small"
                    style={{ padding: 4, marginLeft: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPolicyTextExpanded((prev) => !prev);
                    }}
                    aria-label={policyTextExpanded ? "Ocultar" : "Expandir"}
                  >
                    {policyTextExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </div>
              {(!isMobile || policyTextExpanded) && (
                <span style={{ fontSize: "13px", color: "#333", display: "block" }}>
                  De acordo com as políticas do Meta, você tem 24h para responder após receber uma mensagem.
                </span>
              )}
            </div>
          </div>
          {window24h?.isMetaChannel && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
              {window24h?.isWithinWindow && (
                <>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      backgroundColor: "#25D366",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "13px",
                    }}
                  >
                    <Done style={{ fontSize: 18 }} />
                    {window24h.hoursRemaining}h {window24h.minutesRemaining}min restantes
                  </div>
                  {window24h.closesAt && (
                    <span style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
                      Fecha {format(parseISO(window24h.closesAt), "dd/MM/yyyy")} às {format(parseISO(window24h.closesAt), "HH:mm")}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;